/**
 * Prompt Schema V2 — Anam's five-section structure.
 *
 * Anam's guidance (Tom Ames-Greenall, 4 July 2026): a complex persona prompt
 * should be five clearly separated sections. Keeping them separate is what
 * prevents the contradictory instructions that cause inconsistent behaviour.
 *
 * The canonical order below is Anam's. Do not reorder: "most important first"
 * applies *within* a section, not to the section order itself.
 */
export const PROMPT_SECTIONS = ['personality', 'environment', 'tone', 'goal', 'guardrails'] as const;

export type PromptSectionKey = (typeof PROMPT_SECTIONS)[number];

export const SECTION_HEADINGS: Record<PromptSectionKey, string> = {
  personality: 'Personality',
  environment: 'Environment',
  tone: 'Tone',
  goal: 'Goal',
  guardrails: 'Guardrails',
};

/**
 * Who a piece of data is allowed to reach.
 *
 * This is the invariant that must never be broken:
 *  - 'agent'     — safe to compile into the live avatar prompt
 *  - 'learner'   — shown to the rep BEFORE the session (e.g. objectives).
 *                  Sending it to the avatar makes the agent play along and
 *                  volunteer what the learner is meant to elicit.
 *  - 'evaluator' — used for scoring AFTER the session (e.g. expected answers,
 *                  pass criteria, rubric). Sending it to the avatar leaks
 *                  answers during an examination.
 */
export type FieldAudience = 'agent' | 'learner' | 'evaluator';

/** A single compiled section. `lines` are emitted in order — most important first. */
export interface PromptSection {
  key: PromptSectionKey;
  lines: string[];
}

/** The compiled, type-agnostic prompt. Every adapter produces exactly this. */
export interface PromptV2 {
  sections: Record<PromptSectionKey, string[]>;
  /** Opening line is Anam's `initialMessage` — never duplicated into the prompt. */
  firstMessage?: string;
}

export function emptyPromptV2(): PromptV2 {
  return {
    sections: { personality: [], environment: [], tone: [], goal: [], guardrails: [] },
  };
}
